import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

import { SecurityFinancialTermsDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityFinancialTermsDrawer/SecurityFinancialTermsDrawer.tsx';
import { DraggableSecurityCard } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/components/DraggableSecurityCard.tsx';
import { DroppableRankContainer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/components/DroppableRankContainer.tsx';
import { DroppableRankingContent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/components/DroppableRankingContent.tsx';
import { DroppableSidebar } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/components/DroppableSidebar.tsx';
import {
	DraggedSecurityRankCards,
	SecurityRankingPageContainer,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/SecurityRanking.styles.tsx';
import { securityRankingCollisionDetection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingCollisionDetection.ts';
import {
	DragAndDropId,
	SecurityWithRank,
	UpsertSecurityRankParams,
	VIRTUAL_RESIDUE_RANK_ID,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import { makeSecurityRankId } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingUtils.ts';
import { useSecurityDragAndDrop } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/useSecurityDragAndDrop.tsx';
import { useSecurityRankingController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/useSecurityRankingController.tsx';
import { useSecuritiesController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/useSecuritiesController.tsx';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { RankContainer } from '@dealstack/frontend/shared/components/RankContainer/RankContainer.tsx';
import { RankContainerPlaceholder } from '@dealstack/frontend/shared/components/RankContainer/RankContainerPlaceholder.tsx';
import { SecurityRankCard } from '@dealstack/frontend/shared/components/SecurityRankCard/SecurityRankCard';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	SecurityFeatureType,
	UpsertSecurityIncomeRanksDto,
	UpsertSecurityRankDto,
} from '@dealstack/shared/lib/generated';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Button, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Props {
	selectedEntityLoading?: boolean;
}

export const SecurityRanking = ({ selectedEntityLoading }: Props) => {
	const tCapManage = useTranslationCapManage();
	const trackingSecInNonTrackingRankMsg = tCapManage(
		'securities.ranking.tracking_sec_in_non_tracking_rank'
	);
	const anyOtherSecInTrackingOrResidueRankMsg = tCapManage(
		'securities.ranking.any_other_sec_in_tracking_or_residue_rank'
	);

	const { selectedEntityName, selectedEntityId, dealId } = useSecuritiesController();
	const [splitIds, setSplitIds] = useState<string[] | undefined>(undefined);
	const [financialTermsDrawerSecurity, setFinancialTermsDrawerSecurity] =
		useState<SecurityWithRank | null>(null);

	const {
		personSecurities: serverPersonSecurities,
		personRanks: serverPersonRanks,
		personRanksLoading,
		personSecuritiesUpdatedAt: serverPersonSecuritiesUpdatedAt,
		personRanksUpdatedAt: serverPersonRanksUpdatedAt,
		createRankRequest,
		updateRankRequest,
		deleteRankRequest,
		upsertSecurityRankRequest,
		upsertSecurityIncomeRanksRequest,
		deleteSecurityRankRequest,
		deleteSecurityIncomeRanksRequest,
		refetchPersonRanks,
		refetchPersonSecurities,
	} = useSecurityRankingController(selectedEntityId, splitIds);

	// A liquidation preference forces its security's income right to render split (backend), and it
	// must not be merged back - so the merge control is hidden for any security carrying an LP. WAT-682.
	const lpSecurityIds = useMemo(
		() =>
			new Set(
				(serverPersonSecurities ?? [])
					.filter(
						(sec) =>
							sec.securityFeatureType === SecurityFeatureType.LIQUIDATION_PREFERENCE
					)
					.map((sec) => sec.securityId)
			),
		[serverPersonSecurities]
	);

	const upsertSecurityRank = useCallback(
		(args: UpsertSecurityRankParams) => {
			if (args.isMergedIncomeRight) {
				const payload: UpsertSecurityIncomeRanksDto = {
					securityId: args.securityId,
					rankId: args.rankId,
					issuerId: selectedEntityId,
				};

				upsertSecurityIncomeRanksRequest.mutate(payload, {
					onSuccess: () => {
						void refetchPersonRanks();
						void refetchPersonSecurities();
					},
					onError: () => {
						void refetchPersonSecurities();
					},
				});
			} else {
				const payload: UpsertSecurityRankDto = {
					securityId: args.securityId,
					rankId: args.rankId,
					issuerId: selectedEntityId,
					featureType: args.securityFeatureType,
				};

				upsertSecurityRankRequest.mutate(payload, {
					onSuccess: () => {
						void refetchPersonRanks();
						void refetchPersonSecurities();
					},
					onError: () => {
						void refetchPersonSecurities();
					},
				});
			}
		},
		[
			selectedEntityId,
			upsertSecurityRankRequest,
			upsertSecurityIncomeRanksRequest,
			refetchPersonRanks,
			refetchPersonSecurities,
		]
	);

	const deleteSecurityRank = useCallback(
		(security: SecurityWithRank) => {
			if (security?.isMergedIncomeRight) {
				deleteSecurityIncomeRanksRequest.mutate(security.securityId, {
					onSuccess: () => {
						void refetchPersonSecurities();
					},
					onError: () => {
						void refetchPersonSecurities();
					},
				});
			} else if (security.securityRank?.id) {
				deleteSecurityRankRequest.mutate(security.securityRank.id, {
					onSuccess: () => {
						void refetchPersonSecurities();
					},
					onError: () => {
						void refetchPersonSecurities();
					},
				});
			}
		},
		[deleteSecurityRankRequest, deleteSecurityIncomeRanksRequest, refetchPersonSecurities]
	);

	const mergeSecurityRank = useCallback(
		(securityId: string, rankId: string) => {
			const payload: UpsertSecurityIncomeRanksDto = {
				securityId,
				rankId,
				issuerId: selectedEntityId,
			};

			const dropSplitId = (prev: string[] | undefined) =>
				prev?.filter((id) => id !== securityId) || [];

			upsertSecurityIncomeRanksRequest.mutate(payload, {
				onSuccess: () => {
					setSplitIds(dropSplitId);
					void refetchPersonSecurities();
				},
				onError: () => {
					void refetchPersonSecurities();
				},
			});
		},
		[selectedEntityId, upsertSecurityIncomeRanksRequest, refetchPersonSecurities]
	);

	const securityRankError = upsertSecurityRankRequest.error || deleteSecurityRankRequest.error;

	const {
		sensors,
		dragActiveId,
		dragOverId,
		dragSourceId,
		sortedRanks,
		localRanks,
		unrankedSecurities,
		handleDragStart,
		handleDragOver,
		handleDragEnd,
		handleDragCancel,
		assignSecurityToRankLocally,
		activeSecurity,
		draggedSecurityGroup,
	} = useSecurityDragAndDrop({
		serverPersonRanks,
		serverPersonSecurities,
		serverPersonRanksUpdatedAt,
		serverPersonSecuritiesUpdatedAt,
		selectedEntityId,
		securityRankError,
		upsertSecurityRank,
		deleteSecurityRank,
	});

	// Click-assigns (unranked -> rank, or rank switch) move the card optimistically like drag-drop,
	// then persist - so the card is not gated on refetch timing.
	const assignSecurityToRank = useCallback(
		(security: SecurityWithRank, rankId: string) => {
			assignSecurityToRankLocally(security, rankId);
			upsertSecurityRank({
				securityId: security.securityId,
				securityFeatureType: security.securityFeatureType,
				isMergedIncomeRight: !!security?.isMergedIncomeRight,
				rankId,
			});
		},
		[assignSecurityToRankLocally, upsertSecurityRank]
	);

	// Deep-link support: a security's rank field on the details page links here with
	// ?securityId=&featureType=. Scroll to and briefly highlight the matching card.
	const [searchParams] = useSearchParams();
	const focusSecurityId = searchParams.get('securityId');
	const focusFeatureType = searchParams.get('featureType');
	const focusCardId =
		focusSecurityId && focusFeatureType ? `${focusSecurityId}-${focusFeatureType}` : null;
	const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
	const focusedCardRef = useRef<string | null>(null);
	// DOM node per rendered card, keyed by cardId, populated via each card's onNodeRef.
	const cardNodes = useRef<Map<string, HTMLElement>>(new Map());

	useEffect(() => {
		if (!focusCardId || focusedCardRef.current === focusCardId) {
			return;
		}

		// Exact match, else any card for this security (e.g. a merged income-right card whose feature
		// type differs from the one the link came from).
		let matchedId: string | undefined;
		let node = cardNodes.current.get(focusCardId);
		if (node) {
			matchedId = focusCardId;
		} else if (focusSecurityId) {
			for (const [cardId, element] of cardNodes.current) {
				if (cardId.startsWith(`${focusSecurityId}-`)) {
					matchedId = cardId;
					node = element;
					break;
				}
			}
		}

		if (!node || !matchedId) {
			// Cards may not be registered yet; this effect re-runs once ranking data arrives.
			return;
		}

		focusedCardRef.current = focusCardId;
		node.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setHighlightedCardId(matchedId);
	}, [focusCardId, focusSecurityId, unrankedSecurities, sortedRanks]);

	const registerCardNode = (cardId: string) => (element: HTMLElement | null) => {
		if (element) {
			cardNodes.current.set(cardId, element);
		} else {
			cardNodes.current.delete(cardId);
		}
	};

	useEffect(() => {
		if (!highlightedCardId) {
			return;
		}

		const timer = setTimeout(() => setHighlightedCardId(null), 2500);

		return () => clearTimeout(timer);
	}, [highlightedCardId]);

	const splitUnrankedCard = (securityId: string) => {
		setSplitIds((prev) => {
			if (!prev) {
				return [securityId];
			}

			return [...prev, securityId];
		});
	};

	const mergeRankedCard = (securityId: string, rankId?: string) => {
		if (rankId) {
			mergeSecurityRank(securityId, rankId);
			return;
		}

		const interestSecurity = serverPersonSecurities?.find((sec) => {
			return (
				sec.securityId === securityId &&
				sec.securityFeatureType === SecurityFeatureType.INTEREST
			);
		});
		const principalSecurity = serverPersonSecurities?.find((sec) => {
			return (
				sec.securityId === securityId &&
				sec.securityFeatureType === SecurityFeatureType.PRINCIPAL
			);
		});
		const interestSecurityRank = interestSecurity?.securityRank;
		const principalSecurityRank = principalSecurity?.securityRank;
		const bothCardsUnranked = !interestSecurityRank && !principalSecurityRank;

		if (bothCardsUnranked) {
			setSplitIds((prev) => prev?.filter((id) => id !== securityId) || []);
			void refetchPersonSecurities();
		} else {
			const rankedSecurity = interestSecurityRank ? interestSecurity : principalSecurity;

			if (rankedSecurity) {
				deleteSecurityRank(rankedSecurity);
			}
		}
	};

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.SECURITY_RANKING]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityName,
				dataTestId: TEST_ELEMENT_IDS.CAP_BACK_TO_SECURITIES_BUTTON,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityName]
	);
	const loading = useMemo(
		() =>
			createRankRequest.isPending ||
			updateRankRequest.isPending ||
			deleteRankRequest.isPending ||
			upsertSecurityRankRequest.isPending ||
			deleteSecurityRankRequest.isPending,
		[
			createRankRequest.isPending,
			updateRankRequest.isPending,
			deleteRankRequest.isPending,
			upsertSecurityRankRequest.isPending,
			deleteSecurityRankRequest.isPending,
		]
	);

	useBreadcrumbs(breadcrumbs);

	const createRank = useCallback(() => {
		createRankRequest.mutate(
			{
				displayName: `Rank ${serverPersonRanks.length + 1}`,
				issuerId: selectedEntityId,
			},
			{
				onSuccess: () => {
					void refetchPersonRanks();
				},
			}
		);
	}, [
		createRankRequest,
		serverPersonRanks.length,
		selectedEntityId,
		refetchPersonRanks,
		tCapManage,
	]);

	const updateRank = useCallback(
		(params: { rankId: string; newSortOrder?: number; displayName?: string }) => {
			const { rankId, newSortOrder, displayName } = params;

			updateRankRequest.mutate(
				{
					rankId,
					newSortOrder,
					displayName,
				},
				{
					onSuccess: () => {
						void refetchPersonRanks();
					},
				}
			);
		},
		[updateRankRequest, refetchPersonRanks, tCapManage]
	);

	const deleteRank = useCallback(
		(rankId: string) => {
			deleteRankRequest.mutate(rankId, {
				onSuccess: () => {
					void refetchPersonRanks();
				},
			});
		},
		[deleteRankRequest, refetchPersonRanks, tCapManage]
	);

	const handleRankRemove = useCallback((rankId: string) => deleteRank(rankId), [deleteRank]);

	const handleRankUpdateName = useCallback(
		(rankId: string, newName: string) => {
			updateRank({
				rankId,
				displayName: newName,
			});
		},
		[updateRank]
	);

	const handleRankMoveUp = useCallback(
		(rankId: string, idx: number) => {
			if (idx === 0) {
				return;
			}

			updateRank({
				rankId,
				newSortOrder: sortedRanks[idx - 1].sortOrder,
			});
		},
		[updateRank, sortedRanks]
	);

	const handleRankMoveDown = useCallback(
		(rankId: string, idx: number) => {
			if (
				idx === sortedRanks.length - 1 ||
				// The synthesised residue tier has no server-side sortOrder to swap with.
				sortedRanks[idx + 1].id === VIRTUAL_RESIDUE_RANK_ID
			) {
				return;
			}
			updateRank({
				rankId,
				newSortOrder: sortedRanks[idx + 1].sortOrder,
			});
		},
		[updateRank, sortedRanks]
	);

	const unrankedCards = useMemo(() => {
		return unrankedSecurities.map((sec) => {
			const cardId = makeSecurityRankId(sec);
			return (
				<DraggableSecurityCard
					key={cardId}
					id={cardId}
					draggedFromId={DragAndDropId.sidebar}
					disabled={
						!!sec?.warrantOverId ||
						sec.securityFeatureType === SecurityFeatureType.RESIDUE
					}
					highlighted={highlightedCardId === cardId}
					onNodeRef={registerCardNode(cardId)}
				>
					<SecurityRankCard
						name={sec.securityName}
						securityType={sec.securityType}
						securityFeatureType={sec.securityFeatureType}
						isMergedIncomeRight={!!sec?.isMergedIncomeRight}
						hasRatchetSchedule={!!sec?.hasRatchetSchedule}
						warrantOverName={sec?.warrantOverName}
						canMerge={!lpSecurityIds.has(sec.securityId)}
						onSplitClick={() => splitUnrankedCard(sec.securityId)}
						onMergeClick={() => mergeRankedCard(sec.securityId)}
						onFinancialTermsClick={() => setFinancialTermsDrawerSecurity(sec)}
						cursor="grab"
					/>
				</DraggableSecurityCard>
			);
		});
	}, [unrankedSecurities, highlightedCardId, lpSecurityIds]);
	const unsupportedRankDrop = useMemo<
		| {
				rankId: string;
				message: string;
		  }
		| undefined
	>(() => {
		const securityFeatureType = activeSecurity?.securityFeatureType;
		const matchingRank = sortedRanks?.find((rank) => rank.id === dragOverId);
		const trackingRankExists = sortedRanks?.some((rank) => rank.isTracking);

		if (!securityFeatureType || !matchingRank) {
			return;
		}

		// No RESIDUE branch: residue cards are never draggable, so only non-residue features can
		// hover a tier.
		if (
			securityFeatureType === SecurityFeatureType.TRACKING &&
			!matchingRank.isTracking &&
			(!!matchingRank.securities.length || trackingRankExists)
		) {
			return {
				rankId: matchingRank.id,
				message: trackingSecInNonTrackingRankMsg,
			};
		} else if (
			(matchingRank.isTracking || matchingRank.isResidue) &&
			securityFeatureType !== SecurityFeatureType.TRACKING
		) {
			return {
				rankId: matchingRank.id,
				message: anyOtherSecInTrackingOrResidueRankMsg,
			};
		}
	}, [activeSecurity, dragOverId, sortedRanks]);

	return (
		<>
			<DndContext
				sensors={sensors}
				collisionDetection={securityRankingCollisionDetection}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<PageAbsoluteContainer
					data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_RANKING_PAGE_CONTAINER}
				>
					<SecurityRankingPageContainer>
						{selectedEntityLoading && <InlineLoader />}

						<PageHeader pageTitle={tCapManage('securities.ranking.title')}>
							<Button
								variant="default"
								leftSection={<Icon variant="add" size="lg" />}
								onClick={createRank}
								loading={createRankRequest.isPending}
								// The new rank is named from how many already exist, so clicking
								// before that list loads collides with an existing name.
								disabled={personRanksLoading}
								data-testid={TEST_ELEMENT_IDS.CAP_RANKING_ADD_RANK_BUTTON}
							>
								{tCapManage('securities.ranking.add_rank')}
							</Button>
						</PageHeader>

						<div className="ranking-container">
							<div className={`sidebar ${loading ? 'loading' : ''}`}>
								<Text fz="md" fw={600} c="dark.8" className="sidebar-title">
									{tCapManage('securities.ranking.sidebar.title')}
								</Text>

								<DroppableSidebar
									id={DragAndDropId.sidebar}
									className="sidebar-content"
									isOver={dragOverId === DragAndDropId.sidebar}
								>
									{unrankedCards}
								</DroppableSidebar>
							</div>

							<DroppableRankingContent
								id={DragAndDropId.rankingContent}
								className={`ranking-content ${loading ? 'loading' : ''}`}
								isOver={
									dragOverId === DragAndDropId.rankingContent ||
									dragOverId === DragAndDropId.placeholderRank
								}
							>
								{!sortedRanks?.length && <RankContainerPlaceholder />}

								{sortedRanks.map((rank, idx) => {
									if (rank?.isPlaceholder) {
										const isStickyPlaceholder =
											dragSourceId === DragAndDropId.sidebar;

										return (
											<DroppableRankContainer
												key={rank.id}
												id={rank.id}
												isOver={dragOverId === rank.id}
												sticky={isStickyPlaceholder}
											>
												<RankContainerPlaceholder />
											</DroppableRankContainer>
										);
									}

									return (
										<DroppableRankContainer
											key={rank.id}
											id={rank.id}
											isOver={dragOverId === rank.id}
										>
											<RankContainer
												name={rank.displayName}
												hasSecurities={rank.securities.length > 0}
												isTracking={rank.isTracking}
												isResidue={rank.isResidue}
												displaySortOrder={rank.displaySortOrder}
												zIndex={sortedRanks.length - idx}
												onRemove={() => handleRankRemove(rank.id)}
												onUpdateName={(newName) =>
													handleRankUpdateName(rank.id, newName)
												}
												onMoveUp={() => handleRankMoveUp(rank.id, idx)}
												onMoveDown={() => handleRankMoveDown(rank.id, idx)}
												validUnrankedSecurities={
													rank.validUnrankedSecurities
												}
												onUnrankedDropdownItemClick={(security) =>
													assignSecurityToRank(security, rank.id)
												}
												unsupportedMessage={
													unsupportedRankDrop?.rankId === rank.id
														? unsupportedRankDrop.message
														: undefined
												}
												isLastRank={localRanks.length <= 1}
											>
												{rank.securities.map((sec) => {
													const cardId = makeSecurityRankId(sec);
													// Residue cards are locked in the residue rank:
													// not draggable, no rank-switch / unrank.
													const isResidueCard =
														sec.securityFeatureType ===
														SecurityFeatureType.RESIDUE;

													return (
														<DraggableSecurityCard
															key={cardId}
															id={cardId}
															draggedFromId={rank.id}
															disabled={
																!!sec?.warrantOverId ||
																isResidueCard
															}
															highlighted={
																highlightedCardId === cardId
															}
															onNodeRef={registerCardNode(cardId)}
														>
															<SecurityRankCard
																name={sec.securityName}
																securityType={sec.securityType}
																securityFeatureType={
																	sec.securityFeatureType
																}
																isMergedIncomeRight={
																	!!sec?.isMergedIncomeRight
																}
																hasRatchetSchedule={
																	!!sec?.hasRatchetSchedule
																}
																warrantOverName={
																	sec?.warrantOverName
																}
																canMerge={
																	!lpSecurityIds.has(
																		sec.securityId
																	)
																}
																rankedRankId={
																	sec?.securityRank?.rankId
																}
																lockedInRank={isResidueCard}
																ranks={
																	isResidueCard
																		? undefined
																		: sortedRanks
																}
																onRankClick={
																	isResidueCard
																		? undefined
																		: (selectedRank) =>
																				assignSecurityToRank(
																					sec,
																					selectedRank.id
																				)
																}
																onUnrankClick={
																	isResidueCard
																		? undefined
																		: () =>
																				deleteSecurityRank(
																					sec
																				)
																}
																onSplitClick={() =>
																	splitUnrankedCard(
																		sec.securityId
																	)
																}
																onMergeClick={() => {
																	if (sec?.securityRank?.id) {
																		mergeRankedCard(
																			sec.securityId,
																			sec.securityRank.rankId
																		);
																	}
																}}
																onFinancialTermsClick={() =>
																	setFinancialTermsDrawerSecurity(
																		sec
																	)
																}
																cursor={
																	isResidueCard
																		? 'default'
																		: 'grab'
																}
															/>
														</DraggableSecurityCard>
													);
												})}
											</RankContainer>
										</DroppableRankContainer>
									);
								})}
							</DroppableRankingContent>
						</div>
					</SecurityRankingPageContainer>
				</PageAbsoluteContainer>

				<DragOverlay>
					{dragActiveId && draggedSecurityGroup.length > 0 ? (
						<DraggedSecurityRankCards>
							{draggedSecurityGroup.map((sec) => (
								<SecurityRankCard
									key={makeSecurityRankId(sec)}
									name={sec.securityName}
									securityType={sec.securityType}
									securityFeatureType={sec.securityFeatureType}
									isMergedIncomeRight={!!sec.isMergedIncomeRight}
									hasRatchetSchedule={!!sec?.hasRatchetSchedule}
									warrantOverName={sec.warrantOverName}
									dragged={true}
									cursor="grabbing"
								/>
							))}
						</DraggedSecurityRankCards>
					) : null}
				</DragOverlay>
			</DndContext>
			<SecurityFinancialTermsDrawer
				securityCard={financialTermsDrawerSecurity}
				opened={!!financialTermsDrawerSecurity}
				selectedEntityName={selectedEntityName}
				onClose={() => setFinancialTermsDrawerSecurity(null)}
			/>
		</>
	);
};
